import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  GlobalOutlined,
  CloudServerOutlined,
  DesktopOutlined,
  UserOutlined,
  KeyOutlined,
  SafetyCertificateOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import "./Gg88DecryptFlow.css";

export const GG88_ROUTE_NOHU = "/NH";

type Gg88DecryptFlowProps = {
  isOpen: boolean;
  onClose: () => void;
};

type FlowStep = "progress" | "complete";

type LogLine = {
  at: number;
  text: string;
};

const TERMINAL_LOGS: LogLine[] = [
  { at: 0, text: "Chữ ký hash đã được định vị. Chuẩn bị giao thức giải mã..." },
  { at: 8, text: "Đang thực thi thuật toán giải mã..." },
  { at: 28, text: "Thuật toán đang chạy... 25% hoàn tất." },
  { at: 48, text: "Thuật toán đang chạy... 50% hoàn tất." },
  { at: 68, text: "Thuật toán đang chạy... 75% hoàn tất." },
  { at: 82, text: "Giải mã thành công. Đang xóa chữ ký khỏi nhật ký máy chủ..." },
  { at: 94, text: "Chữ ký đã được xóa. Đang làm sạch dấu vết..." },
];

const PROGRESS_DURATION_MS = 7200;

function randomHex(length: number): string {
  const chars = "0123456789ABCDEF";
  return Array.from(
    { length },
    () => chars[Math.floor(Math.random() * chars.length)]
  ).join("");
}

function getDeviceLabel(): string {
  if (typeof navigator === "undefined") return "Desktop/PC";
  return /mobile|android|iphone|ipad/i.test(navigator.userAgent)
    ? "Mobile"
    : "Desktop/PC";
}

function readUsername(): string {
  try {
    const raw = localStorage.getItem("user_info");
    if (!raw) return "—";
    const parsed = JSON.parse(raw) as { userName?: string; username?: string };
    return parsed.userName ?? parsed.username ?? "—";
  } catch {
    return "—";
  }
}

const Gg88DecryptFlow: React.FC<Gg88DecryptFlowProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<FlowStep>("progress");
  const [progress, setProgress] = useState(0);
  const [visibleLogs, setVisibleLogs] = useState<string[]>([]);
  const [signatureHash, setSignatureHash] = useState("");
  const [ipAddress, setIpAddress] = useState("—");

  const username = readUsername();
  const deviceLabel = getDeviceLabel();

  const resetFlow = useCallback(() => {
    setStep("progress");
    setProgress(0);
    setVisibleLogs([]);
    setSignatureHash("");
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetFlow();
      return;
    }

    resetFlow();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, resetFlow]);

  useEffect(() => {
    if (!isOpen || step !== "progress") return;

    const startedAt = Date.now();
    const tick = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const nextProgress = Math.min(
        100,
        Math.round((elapsed / PROGRESS_DURATION_MS) * 100)
      );
      setProgress(nextProgress);
      setVisibleLogs(
        TERMINAL_LOGS.filter((line) => line.at <= nextProgress).map(
          (line) => line.text
        )
      );

      if (nextProgress >= 100) {
        window.clearInterval(tick);
        window.setTimeout(() => {
          setSignatureHash(randomHex(64));
          setStep("complete");
        }, 700);
      }
    }, 80);

    return () => window.clearInterval(tick);
  }, [isOpen, step]);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then((data: { ip?: string }) => {
        if (!cancelled && data.ip) setIpAddress(data.ip);
      })
      .catch(() => {
        if (!cancelled) setIpAddress("103.25.46.122");
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleContinue = () => {
    onClose();
    navigate(GG88_ROUTE_NOHU);
  };

  if (!isOpen) return null;

  const modal =
    step === "progress" ? (
      <div
        className="gg88-decrypt"
        role="dialog"
        aria-modal="true"
        aria-label="Đang giải mã chữ ký"
      >
        <div className="gg88-decrypt__backdrop" aria-hidden />
        <div className="gg88-decrypt__progress-panel">
          <h1 className="gg88-decrypt__hero-title">
            <span>SIGNATURE ERASED</span>
            <span>CLEANING TRACKS</span>
          </h1>

          <div className="gg88-decrypt__progress-track" aria-hidden>
            <div
              className="gg88-decrypt__progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="gg88-decrypt__terminal">
            <div className="gg88-decrypt__terminal-head">
              <span>MOD_DECRYPT_019 // OUTPUT_LOG</span>
              <span className="gg88-decrypt__terminal-controls" aria-hidden>
                <span className="gg88-decrypt__terminal-dot gg88-decrypt__terminal-dot--red" />
                <span className="gg88-decrypt__terminal-dot gg88-decrypt__terminal-dot--green" />
              </span>
            </div>
            <div className="gg88-decrypt__terminal-body">
              {visibleLogs.map((line) => (
                <p key={line} className="gg88-decrypt__terminal-line">
                  &gt; {line}
                </p>
              ))}
              <p className="gg88-decrypt__terminal-line gg88-decrypt__terminal-line--cursor">
                &gt; <span className="gg88-decrypt__cursor" aria-hidden />
              </p>
            </div>
          </div>
        </div>
      </div>
    ) : (
      <div
        className="gg88-decrypt"
        role="dialog"
        aria-modal="true"
        aria-label="Giải mã hoàn tất"
      >
        <div className="gg88-decrypt__backdrop" aria-hidden />
        <div className="gg88-decrypt__complete-panel">
          <span className="gg88-decrypt__mod-tag">MOD_DECRYPT_019</span>

          <h2 className="gg88-decrypt__complete-title">DECRYPTION COMPLETE</h2>

          <div className="gg88-decrypt__info-grid">
            <div className="gg88-decrypt__info-item">
              <GlobalOutlined className="gg88-decrypt__info-icon" />
              <span className="gg88-decrypt__info-label">IP ADDRESS</span>
              <span className="gg88-decrypt__info-value">{ipAddress}</span>
            </div>
            <div className="gg88-decrypt__info-item">
              <CloudServerOutlined className="gg88-decrypt__info-icon" />
              <span className="gg88-decrypt__info-label">ISP</span>
              <span className="gg88-decrypt__info-value">FPT Telecom</span>
            </div>
            <div className="gg88-decrypt__info-item">
              <DesktopOutlined className="gg88-decrypt__info-icon" />
              <span className="gg88-decrypt__info-label">DEVICE</span>
              <span className="gg88-decrypt__info-value">{deviceLabel}</span>
            </div>
            <div className="gg88-decrypt__info-item">
              <UserOutlined className="gg88-decrypt__info-icon" />
              <span className="gg88-decrypt__info-label">TÀI KHOẢN</span>
              <span className="gg88-decrypt__info-value">{username}</span>
            </div>
          </div>

          <div className="gg88-decrypt__info-row">
            <KeyOutlined className="gg88-decrypt__row-icon" />
            <div className="gg88-decrypt__row-content">
              <span className="gg88-decrypt__info-label">SIGNATURE HASH</span>
              <span className="gg88-decrypt__info-value gg88-decrypt__info-value--hash">
                {signatureHash}
              </span>
            </div>
          </div>

          <div className="gg88-decrypt__info-row">
            <SafetyCertificateOutlined className="gg88-decrypt__row-icon" />
            <div className="gg88-decrypt__row-content">
              <span className="gg88-decrypt__info-label">STATUS</span>
              <span className="gg88-decrypt__info-value">SIGNATURE ERASED</span>
            </div>
          </div>

          <p className="gg88-decrypt__notice">
            <CheckOutlined className="gg88-decrypt__notice-icon" />
            Đã xóa mã ẩn cho tài khoản {username}.
          </p>

          <button
            type="button"
            className="gg88-decrypt__continue-btn"
            onClick={handleContinue}
          >
            TIẾP TỤC
          </button>
        </div>
      </div>
    );

  return createPortal(modal, document.body);
};

export default Gg88DecryptFlow;
